import type { QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
	createRootRouteWithContext,
	HeadContent,
	Scripts,
} from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Toaster } from "sonner";
import { Error404Component, GenericError } from "@/components/Error";
import { ClientTelemetry } from "@/components/misc/ClientTelemetry";
import { ThemeProvider } from "@/components/providers/theme-provider";
import mainCss from "@/styles/main.css?url";
import { TailwindSizeDisplay } from "@/utils/breakpoint";
import { schema, toScript } from "@/utils/schema";

export const Route = createRootRouteWithContext<{
	queryClient: QueryClient;
}>()({
	head: () => ({
		meta: [
			{ charSet: "utf-8" },
			{
				name: "viewport",
				content: "width=device-width, initial-scale=1",
			},
		],
		scripts: [toScript(schema.webSite()), toScript(schema.person())],
		links: [
			{ rel: "stylesheet", href: mainCss },
			{
				rel: "apple-touch-icon",
				sizes: "180x180",
				href: "/favicons/apple-touch-icon.png",
			},
			{
				rel: "icon",
				type: "image/png",
				sizes: "32x32",
				href: "/favicons/favicon-32x32.png",
			},
			{
				rel: "icon",
				type: "image/png",
				sizes: "16x16",
				href: "/favicons/favicon-16x16.png",
			},
			{ rel: "manifest", href: "/favicons/site.webmanifest", color: "#fffff" },
			{ rel: "icon", href: "/favicons/favicon.ico" },
		],
	}),
	errorComponent: ({ error, reset }) => (
		<RootShell>
			<GenericError
				title="Something went wrong"
				message={error instanceof Error ? error.message : "Unexpected error"}
				retry={reset}
			/>
		</RootShell>
	),
	notFoundComponent: () => (
		<RootShell>
			<Error404Component />
		</RootShell>
	),
	shellComponent: RootShell,
});

function RootShell({ children }: { children: ReactNode }) {
	return (
		<html lang="en">
			<head>
				<HeadContent />
			</head>
			<body>
				<ThemeProvider>
					{children}
					<ClientTelemetry />
					<TailwindSizeDisplay />
					{import.meta.env.DEV ? (
						<ReactQueryDevtools buttonPosition="bottom-left" />
					) : null}
					<Toaster richColors />
				</ThemeProvider>
				<Scripts />
			</body>
		</html>
	);
}
